import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "@phosphor-icons/react";
import { useLanguage } from "@/i18n/LanguageContext";
import { LivonMark, Butterfly } from "./Brand";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLanguage();

  const scrollToId = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-livon-bg"
    >
      {/* Soft pastel blob background — replaces photographic imagery */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="blob blob-lg animate-float-slow"
             style={{ background: "radial-gradient(circle, #D8CEEA 0%, transparent 70%)", top: "-10%", right: "-10%" }} />
        <div className="blob blob-lg animate-float-slow"
             style={{ background: "radial-gradient(circle, #EFC9BC 0%, transparent 70%)", bottom: "-20%", left: "-10%", animationDelay: "-4s" }} />
        <div className="blob"
             style={{ background: "radial-gradient(circle, #C7DCEA 0%, transparent 70%)", width: "40vw", height: "40vw", top: "30%", left: "30%", opacity: 0.55 }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-24 md:pb-32">
        {/* Floating butterfly anchor — visible from md+ to balance the right half */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:flex absolute top-32 lg:top-40 right-6 lg:right-10 items-center justify-center pointer-events-none"
          aria-hidden="true"
          data-testid="hero-butterfly-anchor"
        >
          <div className="relative">
            {/* concentric rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              {[420, 320, 220].map((s) => (
                <div
                  key={s}
                  className="absolute rounded-full border border-livon-ink/8"
                  style={{ width: s, height: s }}
                />
              ))}
            </div>
            <div className="relative w-56 h-56 lg:w-72 lg:h-72 rounded-full bg-white/70 backdrop-blur-sm border border-livon-line shadow-[0_30px_90px_-30px_rgba(126,111,184,0.35)] flex items-center justify-center">
              <div className="animate-float-slow">
                <Butterfly size={140} />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="show"
          variants={fade}
          custom={1}
          className="serif text-livon-ink leading-[0.92] tracking-tight"
          style={{ fontSize: "clamp(4rem, 14vw, 14rem)", fontWeight: 500 }}
          data-testid="hero-wordmark"
        >
          <LivonMark supSize="0.18em" />
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          custom={2}
          className="serif italic text-livon-ink mt-8 md:mt-10 max-w-3xl"
          style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1.1, fontWeight: 400 }}
          data-testid="hero-tagline"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          custom={3}
          className="mt-8 md:mt-10 max-w-xl text-livon-muted text-base md:text-lg leading-relaxed"
          data-testid="hero-lead"
        >
          {t.hero.lead}
        </motion.p>

        <motion.div
          initial="hidden"
          animate="show"
          variants={fade}
          custom={4}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-method"
            onClick={scrollToId("method")}
            className="group inline-flex items-center justify-center gap-3 bg-livon-ink text-livon-bg px-8 py-4 rounded-full text-sm tracking-widest uppercase hover:bg-livon-ink/90 transition-all duration-300 hover:-translate-y-0.5"
          >
            <span>{t.hero.ctaMethod}</span>
            <ArrowUpRight size={16} weight="light" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-app"
            onClick={scrollToId("app")}
            className="group inline-flex items-center justify-center gap-3 border border-livon-ink/25 text-livon-ink px-8 py-4 rounded-full text-sm tracking-widest uppercase hover:bg-white/60 hover:border-livon-ink/50 transition-all duration-300 hover:-translate-y-0.5"
          >
            <span>{t.hero.ctaApp}</span>
            <ArrowUpRight size={16} weight="light" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="show"
          variants={fade}
          custom={6}
          className="absolute bottom-10 left-6 md:left-10 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-livon-muted"
        >
          <ArrowDown size={14} weight="light" className="animate-bounce" />
          {t.hero.scroll}
        </motion.div>
      </div>
    </section>
  );
};
